import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/withStyles';

import { Row, Col, Label } from 'react-bootstrap';
import { Table, Column, Cell } from 'fixed-data-table-2';

import s from './TermPredictions.css';
import PredictionCell from '../PredictionCell';
import Filter from '../Filter';

/*
 * Table cell that displays term information
 * Includes div to add caret on terms with publications
 *   - Will only display if row has publications
 */

const TermCell = ({ rowIndex, data, col, expanded, hasPubs, ...props }) => {
  let identifier = null;
  const caret =
    expanded &&
    (expanded.has(rowIndex) ? (
      <i className="fa fa-caret-down" />
    ) : (
      <i className="fa fa-caret-right" />
    ));

  if (col === 'title') {
    identifier = (
      <div
        style={
          hasPubs[data[rowIndex].identifier]
            ? { display: 'inline' }
            : { display: 'none' }
        }
      >
        {caret}{' '}
      </div>
    );
  }

  return (
    <Cell {...props}>
      {identifier}
      {data[rowIndex][col]}
    </Cell>
  );
};

TermCell.propTypes = {
  rowIndex: PropTypes.number,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  col: PropTypes.string.isRequired,
  expanded: PropTypes.instanceOf(Set),
  hasPubs: PropTypes.shape({
    term: PropTypes.object,
  }),
};

TermCell.defaultProps = {
  hasPubs: {},
  expanded: null,
  rowIndex: null,
};

/*
 * Component to display a table of terms predicted to a gene
 */

export const TermPredictions = ({ genes, allPredictions, width, height }) => {
  const [selectedCol, setSelectedCol] = useState(2);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const terms = useMemo(() => {
    const reducedTerms = genes.reduce((obj, gene) => {
      if (allPredictions[gene.entrez]) {
        const preds = allPredictions[gene.entrez];
        preds.forEach(pred => {
          Object.assign(obj, { [pred.term.identifier]: pred.term });
        });
      }
      return obj;
    }, {});
    return Object.values(reducedTerms);
  }, [allPredictions, genes]);

  const predictions = useMemo(() => {
    const reducedPredictions = Object.keys(allPredictions).reduce(
      (obj, gene) => {
        const preds = allPredictions[gene];
        Object.assign(obj, {
          [gene]: preds.reduce((obj2, pred) => {
            Object.assign(obj2, { [pred.term.identifier]: pred });
            return obj2;
          }, {}),
        });
        return obj;
      },
      {},
    );
    return reducedPredictions;
  }, [allPredictions]);

  const termPubs = useMemo(() => {
    const pubs = {};
    for (let i = 0; i < genes.length; i += 1) {
      const gene = genes[i].entrez;
      const pred = predictions[gene];
      if (pred) {
        Object.entries(pred).forEach(([term, termPred]) => {
          if (termPred.pubs) {
            if (!pubs[term]) pubs[term] = { gene_count: 0, pub_count: 0 };

            pubs[term].pub_count += termPred.pubs.length;
            pubs[term].gene_count += 1;
          }
        });
      }
    }
    // When the pubs change reset expanded rows
    setExpandedRows(new Set());
    return pubs;
  }, [genes, predictions]);

  const fields = ['title', 'identifier'];
  const saveFields = fields.concat(genes.map(g => `${g.entrez}`));
  const saveHeaders = fields.concat(genes.map(g => g.standard_name));
  const getField = (term, field) => {
    if (field in term) return term[field];
    return predictions[field][term.identifier]
      ? predictions[field][term.identifier].score
      : 0;
  };

  return (
    <div className={s.root}>
      <Row>
        <Col md={12}>
          <FilteredTermPredictionTable
            genes={genes}
            predictions={predictions}
            terms={terms}
            hasPubs={termPubs}
            width={width}
            height={height}
            onColSelect={idx => setSelectedCol(idx)}
            onRowExpansion={rows => setExpandedRows(rows)}
            onSearchFinish={() => setExpandedRows(new Set())}
            selectedCol={selectedCol}
            expandedRows={expandedRows}
            getField={getField}
            saveFields={saveFields}
            saveHeaders={saveHeaders}
            saveFile="term_predictions"
          />
        </Col>
      </Row>
    </div>
  );
};

TermPredictions.propTypes = {
  // The gene that is predicted to the displayed terms
  genes: PropTypes.arrayOf(PropTypes.object),

  allPredictions: PropTypes.shape({
    entrez: PropTypes.object,
  }),

  width: PropTypes.number,
  height: PropTypes.number,
};

TermPredictions.defaultProps = {
  width: 800,
  height: 500,
  genes: [],
  allPredictions: {},
};

const TermPredictionTable = ({
  predictions,
  terms,
  hasPubs,
  genes,
  width,
  height,
  onSort,
  onColSelect,
  onRowExpansion,
  selectedCol,
  expandedRows,
}) => {
  // Callback when a gene column header is clicked
  const selectCol = (col, direction, idx) => {
    onRowExpansion(new Set());
    onSort(col, direction);
    onColSelect(idx);
  };
  // Column components for each gene
  const geneCol = (gene, idx) => (
    <Column
      key={idx}
      header={
        <Cell
          key={idx}
          className={s.link}
          onClick={() => selectCol(gene.entrez, -1, idx + 2)}
        >
          {gene.standard_name} CONFIDENCE{' '}
          {idx + 2 === selectedCol && <i className="fa fa-sort-amount-desc" />}
        </Cell>
      }
      width={170}
      flexGrow={1}
      cell={
        <PredictionCell
          className={s.link}
          data={predictions[gene.entrez]}
          getRow={(rowIndex, data) => data && data[terms[rowIndex].identifier]}
          col="score"
        />
      }
    />
  );

  // Populate the expanded Row with the content and set the styling.
  const expandedContent = rowDetails => {
    if (!expandedRows.has(rowDetails.rowIndex)) {
      return null;
    }
    const tables = [];
    for (let g = 0; g < genes.length; g += 1) {
      if (predictions[genes[g].entrez]) {
        const publications =
          predictions[genes[g].entrez][terms[rowDetails.rowIndex].identifier];
        const output = [];
        if (publications && publications.pubs) {
          publications.pubs.forEach(pub => {
            const url = `https://www.ncbi.nlm.nih.gov/pubmed/${pub.pmid}`;
            const currOutput = {
              gene: genes[g],
              url,
              author: pub.authors,
              database: publications.database.name,
              titles: pub.title,
              journal: pub.journal,
              date: pub.date,
              id: pub.pmid,
              evidence: publications.evidence,
            };
            output.push(currOutput);
          });

          // No publications for this annotation, but there is a known
          // database
          if (!publications.pubs.length && publications.database) {
            let currOutput = { database: publications.database.name };
            if (publications.evidence !== '')
              currOutput = { evidence: publications.evidence };
            output.push(currOutput);
          }
          const tableOutput = {
            geneName: genes[g].standard_name,
            gene: genes[g].entrez,
            output,
            publications,
          };
          tables.push(tableOutput);
        }
      }
    }

    // Stops the expanded row from closing when a link is clicked
    const onLinkClick = e => {
      e.stopPropagation();
    };
    const style = {
      paddingTop: 50,
      paddingRight: 10,
      paddingLeft: 25,
    };

    // Return the tables with the publications
    return (
      <div style={style}>
        {tables.map(table => (
          <div key={table.gene}>
            {genes.length > 1 ? <h4>{table.geneName}</h4> : null}
            <table key={table.gene} width="100%">
              <tbody>
                <tr>
                  <th width="12%">Database</th>
                  {table.publications.evidence !== '' ? (
                    <th width="8%">Evidence</th>
                  ) : null}
                  {table.publications.pubs.length > 0 ? (
                    <th width="80%">Publications</th>
                  ) : null}
                </tr>

                {table.output.map(mapping => (
                  <tr height="30" key={`${mapping.gene},${mapping.url}`}>
                    <td>{mapping.database}</td>
                    <td>
                      <Label bsStyle="success">{mapping.evidence}</Label>
                    </td>
                    <td>
                      <span style={{ fontWeight: 'bold' }}>
                        {mapping.author}
                      </span>{' '}
                      <a
                        onClick={onLinkClick}
                        href={mapping.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {mapping.titles}
                      </a>{' '}
                      <i>{mapping.journal}</i> {mapping.date}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    );
  };

  // The Height of the row, based on number of tables, and number of publications
  const subRowHeight = rowIndex => {
    let subHeight = 0;
    if (expandedRows.has(rowIndex)) {
      const term = terms[rowIndex].identifier;
      // Pub table heights: each gene will have at least one row
      subHeight +=
        Math.max(hasPubs[term].gene_count, hasPubs[term].pub_count) * 34;

      // Add gene name header heights if there are multiple genes
      subHeight += genes.length > 1 ? hasPubs[term].gene_count * 46 : 17;

      // Row height
      subHeight += 40;
    }
    return subHeight;
  };

  // Determine which rows to apply css to
  const rowClassName = rowIndex =>
    hasPubs[terms[rowIndex].identifier] ? s.link : '';

  // Add the selected row to the list of expanded rows if it has a publication
  const rowClicked = (e, rowIndex) => {
    const copyRows = new Set([...expandedRows]);
    if (copyRows.has(rowIndex)) {
      copyRows.delete(rowIndex);
    } else if (hasPubs[terms[rowIndex].identifier]) {
      copyRows.add(rowIndex);
    }
    onRowExpansion(copyRows);
  };

  // Calculate table height, including the heights of the expanded
  // rows
  let tableHeight = terms.length * 40 + 64;
  expandedRows.forEach(idx => {
    tableHeight += subRowHeight(idx);
  });

  return (
    <Table
      rowHeight={40}
      rowsCount={terms.length}
      width={width}
      height={Math.min(tableHeight, height)}
      headerHeight={30}
      onRowClick={rowClicked}
      subRowHeightGetter={subRowHeight}
      rowExpanded={expandedContent}
      rowClassNameGetter={rowClassName}
    >
      <Column
        header={
          <Cell className={s.link} onClick={() => selectCol('title', 1, 0)}>
            NAME {selectedCol === 0 && <i className="fa fa-sort-amount-desc" />}
          </Cell>
        }
        width={255}
        cell={
          <TermCell
            data={terms}
            col="title"
            expanded={expandedRows}
            hasPubs={hasPubs}
          />
        }
        flexGrow={2}
      />
      <Column
        header={
          <Cell
            className={s.link}
            onClick={() => selectCol('identifier', 1, 1)}
          >
            IDENTIFIER{' '}
            {selectedCol === 1 && <i className="fa fa-sort-amount-desc" />}
          </Cell>
        }
        width={120}
        cell={<TermCell data={terms} col="identifier" />}
      />
      {genes.map(geneCol)}
    </Table>
  );
};

TermPredictionTable.propTypes = {
  predictions: PropTypes.shape({
    gene: PropTypes.object,
  }).isRequired,
  terms: PropTypes.arrayOf(PropTypes.object).isRequired,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  onSort: PropTypes.func.isRequired,
  onColSelect: PropTypes.func.isRequired,
  onRowExpansion: PropTypes.func.isRequired,
  selectedCol: PropTypes.number.isRequired,
  genes: PropTypes.arrayOf(PropTypes.object).isRequired,
  hasPubs: PropTypes.shape({
    term: PropTypes.object,
  }).isRequired,
  expandedRows: PropTypes.instanceOf(Set).isRequired,
};

const FilteredTermPredictionTable = Filter({
  dataField: 'terms',
  fields: ['title', 'identifier'],
})(TermPredictionTable);

export default withStyles(s)(TermPredictions);
